import type { DeploymentEvent } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Globe, RotateCcw, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { shareApi } from "@/api/browser.ts";
import { cn } from "@/lib/cn.ts";
import { copyText } from "@/lib/uid.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { Alert, Button } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

const SUB_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * "Stable public domain" for one app port: publish it on <name>.<domain>, rename, copy, open or unpublish it,
 * see the project's other domains, and restore an earlier one from the deployment history.
 */
export function PublishPanel({ projectId, port, onClose }: { projectId: string; port: number; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const deployments = useQuery({ queryKey: ["projects", projectId, "deployments"], queryFn: () => shareApi.deployments(projectId) });
  const history = useQuery({ queryKey: ["projects", projectId, "deployments", "history"], queryFn: () => shareApi.history(projectId) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["projects", projectId, "deployments"] });

  const mine = (deployments.data ?? []).filter((d) => d.port === port);
  const live = mine[0] ?? null;
  const others = (deployments.data ?? []).filter((d) => d !== live);
  const domain = history.data?.domain ?? "";
  const [name, setName] = useState("");
  const [copied, setCopied] = useState(false);
  useEffect(() => { setName(live?.subdomain ?? ""); }, [live?.subdomain]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const publish = useMutation({ mutationFn: () => shareApi.publish(projectId, name, port), onSuccess: refresh });
  const rename = useMutation({ mutationFn: () => shareApi.rename(projectId, live!.subdomain, name), onSuccess: refresh });
  const unpublish = useMutation({ mutationFn: (sub: string) => shareApi.unpublish(projectId, sub), onSuccess: refresh });
  const restore = useMutation({ mutationFn: (id: string) => shareApi.restore(projectId, id), onSuccess: refresh });
  const err = publish.error ?? rename.error ?? unpublish.error ?? restore.error;

  const valid = SUB_RE.test(name);
  const changed = !!live && name !== live.subdomain;
  const submit = () => { if (!valid) return; if (!live) publish.mutate(); else if (changed) rename.mutate(); };
  const askUnpublish = async (sub: string, url: string) => { if (await confirm({ title: t("deployment.unpublishTitle"), message: t("deployment.unpublishMsg", { url }), confirmLabel: t("deployment.unpublish"), danger: true })) unpublish.mutate(sub); };
  const askRestore = async (ev: DeploymentEvent) => { if (await confirm({ title: t("deployment.restore"), message: t("deployment.restoreConfirm", { url: ev.url, port: ev.port }), confirmLabel: t("deployment.restore") })) restore.mutate(ev.id); };
  const copy = async (url: string) => { await copyText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  const events = history.data?.events ?? [];

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 sm:items-center sm:p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={t("deployment.title")} onClick={(e) => e.stopPropagation()} className="flex max-h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl border border-border bg-surface shadow-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 font-semibold"><Globe className="size-4 text-accent" /> {t("deployment.action")} <span dir="ltr" className="rounded bg-surface-2 px-1.5 font-mono text-xs text-fg-muted">:{port}</span></div>
          <button type="button" onClick={onClose} className="tap grid place-items-center rounded-lg p-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg" aria-label={t("common.close")}><X className="size-4" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          {/* stable public domain */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-accent">{t("deployment.title")}</h3>
            <form className="flex flex-wrap items-stretch gap-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <div dir="ltr" className={cn("flex min-w-[14rem] flex-1 overflow-hidden rounded-lg border bg-bg focus-within:border-accent", name && !valid ? "border-danger" : "border-border")}>
                <input value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="my-app" maxLength={63} aria-label={t("deployment.title")} className="min-w-0 flex-1 bg-transparent px-3 py-2.5 font-mono text-sm outline-none" />
                <span className="flex items-center border-s border-border bg-surface-2 px-3 font-mono text-sm text-fg-muted">.{domain}</span>
              </div>
              {!live ? (
                <Button type="submit" loading={publish.isPending} disabled={!valid}><Globe className="size-4" /> {t("deployment.publish")}</Button>
              ) : (
                <>
                  <Button type="submit" variant={changed ? "primary" : "secondary"} loading={rename.isPending} disabled={!changed || !valid}><Globe className="size-4" /> {t("deployment.update")}</Button>
                  <Button type="button" variant="secondary" onClick={() => void copy(live.url)}>{copied ? <Check className="size-4" /> : <Copy className="size-4" />} {t("deployment.copy")}</Button>
                  <a href={live.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md border border-border bg-surface-2 px-3 text-sm font-medium hover:bg-surface"><ExternalLink className="size-4" /> {t("deployment.open")}</a>
                  <Button type="button" variant="danger" loading={unpublish.isPending && unpublish.variables === live.subdomain} onClick={() => void askUnpublish(live.subdomain, live.url)}><Trash2 className="size-4" /> {t("deployment.unpublish")}</Button>
                </>
              )}
            </form>
            <p className="text-xs text-fg-muted">{live ? <>{t("deployment.activePrefix")} <a dir="ltr" href={live.url} target="_blank" rel="noreferrer" className="text-accent underline">{live.url}</a>. {t("deployment.activeSuffix")}</> : t("deployment.help", { port })}</p>
            {err && <Alert tone="danger">{toMsg(err)}</Alert>}
          </section>

          {/* other domains in this project */}
          {others.length > 0 && (
            <section className="space-y-2 border-t border-border pt-4">
              <h3 className="text-sm font-semibold">{t("deployment.othersTitle", { count: others.length })}</h3>
              <ul className="space-y-2">
                {others.map((d) => (
                  <li key={d.subdomain} className="flex items-center gap-2 rounded-lg border border-border bg-bg px-3 py-2">
                    <span dir="ltr" className="min-w-0 flex-1 truncate font-mono text-sm">{d.subdomain}.{domain} <span className="text-fg-muted">→ :{d.port}</span></span>
                    <a href={d.url} target="_blank" rel="noreferrer" className="tap grid place-items-center rounded-md border border-border p-2 text-fg-muted hover:text-fg" aria-label={t("deployment.open")}><ExternalLink className="size-4" /></a>
                    <button type="button" onClick={() => void askUnpublish(d.subdomain, d.url)} className="tap grid place-items-center rounded-md border border-danger/40 p-2 text-danger hover:bg-danger-soft" aria-label={t("deployment.unpublish")}><Trash2 className="size-4" /></button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* deployment history */}
          <section className="space-y-2 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t("deployment.history")}</h3>
              <span className="text-xs text-fg-muted">{t("deployment.historyCount", { count: events.length })}</span>
            </div>
            {events.length === 0 && <div className="rounded-lg border border-dashed border-border px-3 py-3 text-sm text-fg-muted">{t("deployment.noHistory")}</div>}
            <ul className="space-y-2">
              {events.map((ev) => (
                <li key={ev.id} className="flex items-center gap-3 rounded-lg border border-border bg-bg px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{t(`deployment.actions.${ev.action}`)}</span>
                      {ev.current && <span className="rounded-md bg-accent-soft px-1.5 py-0.5 text-[11px] text-accent">{t("deployment.current")}</span>}
                    </div>
                    <div dir="ltr" className="truncate text-start font-mono text-xs text-fg-muted">{ev.subdomain}.{domain}:{ev.port}{ev.previous ? `  ← ${ev.previous}` : ""}</div>
                    <div className="text-[11px] text-fg-muted">{fmt(ev.createdAt)}</div>
                  </div>
                  {ev.action !== "unpublish" && (
                    <Button type="button" size="sm" variant="secondary" disabled={ev.current} loading={restore.isPending && restore.variables === ev.id} onClick={() => void askRestore(ev)}><RotateCcw className="size-3.5" /> {t("deployment.restore")}</Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
